import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Crown, Sparkles, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface PaywallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature?: string;
  /** Quando true, mostra a variante "cota esgotada" com copy específica */
  quotaExhausted?: boolean;
  /** Tipo de cota esgotada (apenas visual) */
  quotaType?: "tutor" | "quiz";
  /** Limite usado (ex: 5) — para personalizar o texto */
  quotaLimit?: number;
}

type Plan = "monthly" | "yearly";

const BENEFITS = [
  {
    title: "Trilhas completas: Power BI, SQL e Python",
    description: "Lições profundas + exercícios infinitos com IA",
  },
  {
    title: "Tutor IA 24/7 nas questões",
    description: "Pergunte, peça outro exemplo, receba explicações no seu contexto",
  },
  {
    title: "Simulador de entrevistas IA ilimitado",
    description: "Técnicas (SQL, Python, Power BI) e comportamentais com feedback",
  },
  {
    title: "Desafios de código avaliados por IA",
    description: "Problemas reais com correção automática e detalhada",
  },
  {
    title: "Analisador de currículo IA",
    description: "Compare seu CV com a vaga e receba sugestões objetivas",
  },
  {
    title: "Certificados verificáveis publicamente",
    description: "Compartilhe no LinkedIn — recrutadores podem validar online",
  },
];

export function PaywallDialog({ open, onOpenChange, feature, quotaExhausted, quotaType = "tutor", quotaLimit }: PaywallDialogProps) {
  const [plan, setPlan] = useState<Plan>("yearly");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke("create-academy-checkout", {
        body: { plan },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("URL de checkout não retornada");
      window.open(data.url, "_blank");
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({
        title: "Erro ao iniciar checkout",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const monthlyPrice = 49;
  const yearlyMonthlyEquivalent = 32.5; // 390/12
  const yearlyTotal = 390;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {quotaExhausted ? (
            <>
              <Badge className="w-fit bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 mb-1">
                Cota grátis esgotada
              </Badge>
              <DialogTitle className="text-2xl">
                {quotaType === "quiz"
                  ? `Você usou sua geração de questões grátis de hoje`
                  : `Você usou suas ${quotaLimit ?? 5} interações grátis com o Tutor IA hoje`}
              </DialogTitle>
              <DialogDescription>
                Assine PRO para continuar praticando agora — sem esperar o reset diário. Tutor IA, questões infinitas e certificados verificáveis.
              </DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="text-2xl">
                {feature ? `Desbloqueie ${feature}` : "Acelere sua carreira em dados"}
              </DialogTitle>
              <DialogDescription>
                Não vendemos vídeos. Vendemos prática até virar fluência — com tutor IA, simulações ilimitadas e certificados verificáveis.
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {/* Toggle de planos */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <button
            type="button"
            onClick={() => setPlan("monthly")}
            className={cn(
              "rounded-lg border-2 p-4 text-left transition-all",
              plan === "monthly"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/40"
            )}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold">Mensal</span>
              {plan === "monthly" && <Check className="h-4 w-4 text-primary" />}
            </div>
            <div className="mt-2">
              <span className="text-2xl font-bold">R$ {monthlyPrice}</span>
              <span className="text-sm text-muted-foreground">/mês</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Cancele quando quiser</p>
          </button>

          <button
            type="button"
            onClick={() => setPlan("yearly")}
            className={cn(
              "relative rounded-lg border-2 p-4 text-left transition-all",
              plan === "yearly"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/40"
            )}
          >
            <Badge className="absolute -top-2 right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
              Economize 33%
            </Badge>
            <div className="flex items-center justify-between">
              <span className="font-semibold">Anual</span>
              {plan === "yearly" && <Check className="h-4 w-4 text-primary" />}
            </div>
            <div className="mt-2">
              <span className="text-2xl font-bold">R$ {yearlyMonthlyEquivalent.toFixed(2).replace(".", ",")}</span>
              <span className="text-sm text-muted-foreground">/mês</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              R$ {yearlyTotal} cobrados anualmente
            </p>
          </button>
        </div>

        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Crown className="h-5 w-5 text-primary" />
              O que está incluso no PRO
            </CardTitle>
            <CardDescription>Acesso completo enquanto a assinatura estiver ativa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex items-start gap-3">
                <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.description}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Voltar
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Abrindo checkout...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Assinar PRO {plan === "yearly" ? "Anual" : "Mensal"}
              </>
            )}
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Pagamento seguro via Stripe. Você pode cancelar a qualquer momento.
        </p>
      </DialogContent>
    </Dialog>
  );
}
